import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { NotificationService } from '../services/errorService';

export interface DashboardNotification {
  id: string;
  type: 'deal' | 'order' | 'inventory' | 'wealth' | 'system' | 'info' | 'success' | 'warning' | 'error' | 'market' | 'marketplace';
  title: string;
  message: string;
  read: boolean;
  link?: string;
  created_at: string;
  metadata?: Record<string, any> | null;
}

interface DashboardNotificationContextType {
  notifications: DashboardNotification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Omit<DashboardNotification, 'id' | 'read' | 'created_at'>) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  loading: boolean;
}

const DashboardNotificationContext = createContext<DashboardNotificationContextType | undefined>(undefined);

export const DashboardNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch notifications from Supabase
  const fetchNotifications = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      // Transform DB fields to match interface if needed
      const transformed = data?.map(n => ({
        ...n,
        read: Boolean((n as any).is_read ?? (n as any).read ?? false),
        metadata: (n as any).metadata && typeof (n as any).metadata === 'object' ? (n as any).metadata : null,
      })) || [];
      
      setNotifications(transformed);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();

      // Real-time subscription
      const channel = supabase
        .channel(`dashboard-notifications-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            const newNotif = {
              ...payload.new,
              read: Boolean((payload.new as any).is_read ?? (payload.new as any).read ?? false),
              metadata:
                (payload.new as any).metadata && typeof (payload.new as any).metadata === 'object'
                  ? (payload.new as any).metadata
                  : null,
            } as DashboardNotification;
            
            setNotifications(prev => [newNotif, ...prev]);

            // Trigger UI snackbar
            NotificationService.show({
              type: ['success', 'error', 'warning', 'info'].includes(newNotif.type) ? newNotif.type as any : 'info',
              title: newNotif.title,
              message: newNotif.message,
              duration: 8000
            });
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    } else {
      setNotifications([]);
      setLoading(false);
    }
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true, read: true })
        .eq('id', id);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true, read: true })
        .eq('user_id', user.id)
        .or('is_read.eq.false,read.eq.false');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const addNotification = async (notif: Omit<DashboardNotification, 'id' | 'read' | 'created_at'>) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          title: notif.title,
          message: notif.message,
          type: notif.type,
          link: notif.link,
          metadata: notif.metadata ?? null,
          is_read: false,
          read: false
        })
        .select()
        .single();

      if (error) throw error;
      // State is updated via Realtime channel
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    try {
      await supabase
        .from('notifications')
        .delete()
        .eq('id', id);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <DashboardNotificationContext.Provider value={{
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      addNotification,
      deleteNotification,
      loading
    }}>
      {children}
    </DashboardNotificationContext.Provider>
  );
};

export const useDashboardNotifications = () => {
  const context = useContext(DashboardNotificationContext);
  if (context === undefined) {
    throw new Error('useDashboardNotifications must be used within a DashboardNotificationProvider');
  }
  return context;
};
